import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { cn, formatPercent } from '@/lib/utils';
import { Progress } from '@/components/ui/progress';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { Pause, AlertCircle, Landmark, Users, CalendarClock, UserPlus } from 'lucide-react';
import type { BoardCard as BoardCardType, BoardColumnKey } from '../types';

interface BoardCardProps {
  card: BoardCardType;
  column: BoardColumnKey;
  onClick?: () => void;
  /**
   * Contrôle d'assignation affiché à la place du propriétaire (admin). Le clic
   * y est isolé pour ne pas déclencher la navigation de la carte.
   */
  assignSlot?: ReactNode;
  /**
   * Contrôle d'édition de la date de migration prévue (admin). Affiché à la
   * place du texte en lecture seule ; le clic y est isolé de la navigation.
   */
  plannedSlot?: ReactNode;
}

const COLUMN_BAR_COLOR: Record<BoardColumnKey, string> = {
  todo: 'bg-gray-400',
  inProgress: 'bg-violet-500',
  blocked: 'bg-red-500',
  completed: 'bg-emerald-500',
};

const COLUMN_PROGRESS_CLASS: Record<BoardColumnKey, string> = {
  todo: '[&>div]:bg-gray-400',
  inProgress: '[&>div]:bg-violet-500',
  blocked: '[&>div]:bg-red-500',
  completed: '[&>div]:bg-emerald-500',
};

const COLUMN_TEXT_COLOR: Record<BoardColumnKey, string> = {
  todo: 'text-gray-600',
  inProgress: 'text-violet-600',
  blocked: 'text-red-600',
  completed: 'text-emerald-600',
};

function initialsOf(firstname: string, lastname: string): string {
  const f = firstname?.[0] ?? '';
  const l = lastname?.[0] ?? '';
  return `${f}${l}`.toUpperCase() || '—';
}

function shortName(firstname: string, lastname: string): string {
  if (!firstname && !lastname) return '—';
  const last = lastname ? `${lastname[0]}.` : '';
  return `${firstname ?? ''} ${last}`.trim();
}

function relativeTime(iso: string | null): string {
  if (!iso) return '—';
  const date = new Date(iso);
  const now = Date.now();
  const diffMs = now - date.getTime();
  const minutes = Math.floor(diffMs / 60000);
  if (minutes < 1) return "à l'instant";
  if (minutes < 60) return `il y a ${minutes} min`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `il y a ${hours} h`;
  // Show today / yesterday / date
  const days = Math.floor(hours / 24);
  if (days === 1) {
    return `hier ${date.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}`;
  }
  if (days < 7) {
    return `il y a ${days} j`;
  }
  return date.toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit' });
}

/** Formate une date planifiée (AAAA-MM-JJ) en libellé court français. */
function formatPlannedDate(iso: string | null): string {
  if (!iso) return '—';
  // Parse en date locale « nue » (pas d'UTC) pour éviter tout décalage de jour.
  const [y, m, d] = iso.split('-').map(Number);
  const date = new Date(y, (m ?? 1) - 1, d ?? 1);
  return date.toLocaleDateString('fr-FR', { weekday: 'short', day: '2-digit', month: 'short' });
}

export function BoardCard({ card, column, onClick, assignSlot, plannedSlot }: BoardCardProps) {
  const { school, project } = card;

  // Progress
  const totalSteps = project?.totalSteps ?? 0;
  const completedSteps = project?.completedSteps ?? 0;
  const percent = project?.progressPercent ?? (
    totalSteps > 0 ? (completedSteps / totalSteps) * 100 : 0
  );
  const hasProgress = !!project && totalSteps > 0;

  const assignedTo = project?.assignedTo ?? null;
  const plannedDate = project?.plannedMigrationDate ?? null;
  const updatedAt = project?.updatedAt ?? project?.startedAt ?? project?.createdAt ?? null;

  // Ajouts automatiques d'utilisateurs : la V1 a continué d'inscrire des élèves
  // après la bascule, la tâche programmée les a repris. Le détail nominatif
  // (date, heure, personnes) est dans le rapport de migration du dossier.
  const autoCatchUp = project?.autoCatchUp ?? null;
  const autoAdded = autoCatchUp?.added ?? 0;

  return (
    <div
      onClick={onClick}
      className={cn(
        'bg-white dark:bg-slate-800/50 rounded-lg border border-gray-200 dark:border-slate-700 shadow-sm p-3 space-y-3 transition-all',
        onClick && 'cursor-pointer hover:shadow-md hover:border-gray-300 dark:hover:border-slate-600'
      )}
    >
      {/* Header: title + code (right) + EN badge + students */}
      <div className="min-w-0">
        <div className="flex items-start justify-between gap-2">
          <span className="font-semibold text-sm text-gray-900 dark:text-gray-100 leading-tight line-clamp-2 min-w-0">
            {school.name}
          </span>
          <span className="inline-flex items-center shrink-0 rounded-sm border border-gray-200 dark:border-slate-600 bg-gray-50 dark:bg-slate-700/50 px-1 font-mono text-[9px] leading-tight font-medium text-gray-500 dark:text-gray-400 mt-0.5">
            {school.code}
          </span>
        </div>
        <div className="flex items-center gap-1.5 mt-1 flex-wrap">
          <span className="inline-flex items-center gap-1 text-xs text-gray-700 dark:text-gray-200">
            <Users className="h-3 w-3 text-gray-500 dark:text-gray-400" />
            {school.studentCount != null ? (
              <>
                <strong className="text-gray-900 dark:text-gray-100 font-semibold">
                  {school.studentCount}
                </strong>{' '}
                <span className="text-gray-700 dark:text-gray-300 font-normal">élèves</span>
              </>
            ) : (
              <span className="text-gray-500 dark:text-gray-400 font-normal italic">élèves —</span>
            )}
          </span>
          {school.isEducationNationale && (
            <span
              className="inline-flex items-center gap-0.5 rounded border border-blue-200 bg-blue-50 px-1 py-px text-[10px] font-medium text-blue-700 dark:border-blue-900 dark:bg-blue-950/40 dark:text-blue-300"
              title="École Éducation Nationale"
            >
              <Landmark className="h-2.5 w-2.5" />
              EN
            </span>
          )}
        </div>
      </div>

      {/* Progress (only when there's a project with steps) */}
      {hasProgress && (
        <div className="rounded-md bg-gray-50 dark:bg-slate-900/40 p-2.5 space-y-1.5">
          <div className="flex items-end justify-between gap-2">
            <span className="text-xs text-gray-500 dark:text-gray-400 pb-0.5">
              <strong className="text-gray-700 dark:text-gray-200 font-semibold">
                {completedSteps}
              </strong>{' '}
              / {totalSteps} lots
            </span>
            <span className={cn('text-2xl font-bold leading-none', COLUMN_TEXT_COLOR[column])}>
              {formatPercent(percent)}%
            </span>
          </div>
          <Progress
            value={percent}
            className={cn('h-2', COLUMN_PROGRESS_CLASS[column])}
          />
        </div>
      )}

      {/* Blocked badges */}
      {column === 'blocked' && project && (
        <div
          className={cn(
            'rounded-md text-xs px-2 py-1.5 border flex items-start gap-1.5',
            project.status === 'paused'
              ? 'bg-orange-50 border-orange-200 text-orange-700'
              : 'bg-red-50 border-red-200 text-red-700'
          )}
        >
          {project.status === 'paused' ? (
            <>
              <Pause className="h-3 w-3 mt-0.5 shrink-0" />
              <span>
                <strong>En pause</strong> · Mis en pause volontairement
              </span>
            </>
          ) : (
            <>
              <AlertCircle className="h-3 w-3 mt-0.5 shrink-0" />
              <span>
                <strong>Bloquée</strong>
                {project.failedRecords > 0 && ` · ${project.failedRecords} enregistrement(s) en erreur`}
              </span>
            </>
          )}
        </div>
      )}

      {/* Ajouts automatiques d'utilisateurs (rattrapage des inscriptions V1) */}
      {autoAdded > 0 && project && (
        <Link
          to={`/migration-v1-v2/rattrapage/ecoles/${project.id}`}
          // La carte entière ouvre le dossier : ce lien mène à l'historique du
          // rattrapage, il ne doit pas déclencher les deux.
          onClick={(e) => e.stopPropagation()}
          className="flex items-start gap-1.5 rounded-md border border-sky-200 bg-sky-50 px-2 py-1.5 text-xs text-sky-800 hover:bg-sky-100 dark:border-sky-900 dark:bg-sky-950/30 dark:text-sky-200 dark:hover:bg-sky-900/40"
          title={`${autoAdded} utilisateur(s) inscrit(s) en V1 après la migration ont été ajoutés automatiquement${
            autoCatchUp?.lastRunAt ? ` — dernier ajout ${relativeTime(autoCatchUp.lastRunAt)}` : ''
          }. Cliquer pour voir le détail daté.`}
        >
          <UserPlus className="mt-0.5 h-3 w-3 shrink-0" />
          <span>
            <strong className="font-semibold">
              {autoAdded} ajout{autoAdded > 1 ? 's' : ''} auto
            </strong>
            {autoCatchUp?.lastRunAt && (
              <span className="text-sky-700 dark:text-sky-300"> · {relativeTime(autoCatchUp.lastRunAt)}</span>
            )}
            {(autoCatchUp?.failed ?? 0) > 0 && (
              <span className="text-red-700 dark:text-red-300"> · {autoCatchUp!.failed} en erreur</span>
            )}
          </span>
        </Link>
      )}

      {/* Horodatage : juste au-dessus de la ligne responsable / date prévue */}
      <div className="flex justify-end">
        <span className="text-xs text-gray-400">{relativeTime(updatedAt)}</span>
      </div>

      {/* Footer: responsable (gauche) + date de migration prévue (droite) */}
      <div className="flex items-center justify-between gap-2 pt-1.5 border-t border-gray-100 dark:border-slate-700">
        {/* Responsable */}
        {assignSlot ? (
          <div className="min-w-0 flex-1" onClick={(e) => e.stopPropagation()}>
            {assignSlot}
          </div>
        ) : (
          <div className="flex items-center gap-1.5 min-w-0">
            {assignedTo ? (
              <>
                <Avatar className="h-5 w-5">
                  <AvatarFallback className="text-[10px] font-medium bg-gray-200 text-gray-700">
                    {initialsOf(assignedTo.firstname, assignedTo.lastname)}
                  </AvatarFallback>
                </Avatar>
                <span className="text-xs text-gray-600 dark:text-gray-300 truncate">
                  {shortName(assignedTo.firstname, assignedTo.lastname)}
                </span>
              </>
            ) : (
              <span className="text-xs text-gray-400 italic">Non assigné</span>
            )}
          </div>
        )}

        {/* Date de migration prévue, alignée au responsable */}
        {plannedSlot ? (
          <div className="shrink-0" onClick={(e) => e.stopPropagation()}>
            {plannedSlot}
          </div>
        ) : (
          <span className="inline-flex items-center gap-1 shrink-0 text-xs">
            <CalendarClock className="h-3.5 w-3.5 text-gray-400" />
            {plannedDate ? (
              <span className="font-medium text-gray-700 dark:text-gray-200">
                {formatPlannedDate(plannedDate)}
              </span>
            ) : (
              <span className="italic text-gray-400">non planifiée</span>
            )}
          </span>
        )}
      </div>
    </div>
  );
}

export { COLUMN_BAR_COLOR };
